import type { Carer } from '@lmc/ts-common'
import { Chevron } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { ListItem } from 'components/generic'
import type { SuggestionDecision } from 'hooks/useNeedSuggestions'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { formatDate, NeedsIcon } from './NeedItem'

interface StagedNeedItemProps {
  /** A staged "add" decision — the suggestion accepted but not yet saved. */
  decision: SuggestionDecision
  testId?: string
}

/**
 * Draft row for a used suggestion, rendered in the linked-needs list and
 * styled like a real need (per the Figma prototype). It only exists locally
 * until the review is saved; Cancel discards it.
 */
const StagedNeedItem: React.FC<StagedNeedItemProps> = ({
  decision,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [showNotes, setShowNotes] = useState(false)

  const currentUserName: string = useSelector(
    (state: {
      data: { carers?: Carer[] }
      global: { user?: { id?: string } }
    }) => {
      const me = state.data.carers?.find((c) => c.id === state.global.user?.id)
      return me ? `${me.name.first} ${me.name.last}` : '-'
    }
  )

  const { suggestion } = decision
  const note = decision.editedNote ?? suggestion.note

  return (
    <StagedNeedWrapper>
      <ListItem
        hasBorder
        borderRadius={20}
        style={{ padding: '8px 16px' }}
        renderImage={() => (
          <IconBox>
            <NeedsIcon size={32} />
          </IconBox>
        )}
        label={suggestion.need}
        subLabel={t(carePlansConstants.NEED_CREATED_BY, {
          date: formatDate(decision.stagedOn),
          name: currentUserName
        })}
        multiSelect
        dataTestId={testId || 'staged-need-item'}
        renderBody={
          note
            ? () => (
                <NotesBody>
                  <NotesToggle
                    type="button"
                    aria-expanded={showNotes}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      setShowNotes((prev) => !prev)
                    }}
                  >
                    {showNotes
                      ? t(carePlansConstants.NEED_HIDE_NOTES)
                      : t(carePlansConstants.NEED_SHOW_NOTES)}
                    <Chevron direction={showNotes ? 'up' : 'down'} />
                  </NotesToggle>
                  {showNotes && <NotesContent>{note}</NotesContent>}
                </NotesBody>
              )
            : undefined
        }
      />
    </StagedNeedWrapper>
  )
}

const StagedNeedWrapper = styled.div`
  .lmc-list-item-link-container {
    cursor: default !important;
  }

  .lmc-list-item:hover
    > .lmc-list-item-header
    > .lmc-list-item-link-container
    > .lmc-list-item-parent-link
    > .lmc-list-item-link {
    color: inherit !important;
  }
`

/* Colours come from the ui-kit theme, matching NeedItem: this draft row sits
   in the linked-needs list beside the real needs it renders. */
const IconBox = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.color.neutral.color10};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const NotesBody = styled.div`
  padding: 4px 0 0 50px;
`

const NotesToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.status.primary};
  user-select: none;

  &:hover {
    color: ${({ theme }) =>
      theme.color.hover?.primary || theme.color.status.primary};
  }

  svg {
    width: 12px;
    height: 12px;
    stroke-width: 2;
  }
`

const NotesContent = styled(Text)`
  display: block;
  margin-top: 4px;
  color: ${({ theme }) => theme.color.neutral.color3};
  white-space: pre-wrap;
  font-size: 14px;
`

export default StagedNeedItem
